import type {
  Accounts,
  MappedAccountsOfGroup,
  ReservedPostfixes,
} from '../../shared-types'
import { getAccountRangeAndPostfix } from './account-numbers'

const firstAvailableNumberInRange = (numbers: Array<number>): number => {
  if (numbers.length === 0) {
    return 0
  }

  const sortedNumbers = [...numbers].sort((a, b) => a - b)

  for (let i = 0; i <= sortedNumbers.length; i++) {
    if (i !== sortedNumbers[i]) {
      return i
    }
  }

  return sortedNumbers.length
}

const postfixResolver = (
  accountRange: string,
  mappedAccounts: MappedAccountsOfGroup,
  reservedPostfixes?: Array<number>
): {
  getNameWithPostfix: () => string
} => {
  const usedPostFixesFromMappings = mappedAccounts
    ? Object.values(mappedAccounts).reduce((accumulator, mapping) => {
        const [, postfix] = getAccountRangeAndPostfix(mapping.mappedNumber)
        if (postfix) {
          accumulator.push(parseInt(postfix))
        }

        return accumulator
      }, [] as number[])
    : []

  const usedPostFixes = [
    ...new Set(usedPostFixesFromMappings.concat(reservedPostfixes || [])),
  ]

  const getNameWithPostfix = (): string => {
    if (!accountRange) {
      return ''
    }

    if (usedPostFixes.length === 0) {
      usedPostFixes.push(0)
      return `${accountRange}.000`
    }

    const index = firstAvailableNumberInRange(usedPostFixes)
    usedPostFixes.push(index)

    return `${accountRange}.${index.toString().padStart(3, '0')}`
  }

  return { getNameWithPostfix }
}

const calculateReservedPostfixes = (accounts: Accounts): ReservedPostfixes => {
  return Object.values(accounts).reduce((accumulator, account) => {
    const { initialMappedNumber } = account
    const [accountRange, postfix] =
      getAccountRangeAndPostfix(initialMappedNumber)

    if (accountRange && postfix) {
      accumulator[accountRange] = accumulator[accountRange] || []
      accumulator[accountRange].push(parseInt(postfix))
    }

    return accumulator
  }, {} as ReservedPostfixes)
}

const mappedNumberResolver = ({
  accountRange,
  mappedAccounts,
  reservedPostfixes,
  shouldUsePostfixes,
}: {
  accountRange: string
  mappedAccounts: MappedAccountsOfGroup
  reservedPostfixes: Array<number>
  shouldUsePostfixes: boolean
}): {
  generate: () => string
} => {
  if (shouldUsePostfixes) {
    const { getNameWithPostfix } = postfixResolver(
      accountRange,
      mappedAccounts,
      reservedPostfixes
    )

    return {
      generate: getNameWithPostfix,
    }
  }

  return {
    generate: () => accountRange,
  }
}

export { calculateReservedPostfixes, mappedNumberResolver, postfixResolver }
